import { Package, Plus, X, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { findTable } from "@/lib/sizing-store";

/**
 * WizardLinkedProducts — compact linked-products panel used inside
 * AddTableWizard step 3. Same visual language as the edit drawer's "Bağlı
 * ürünler" block, but products are held as a pending set — real
 * `linkProduct` calls fire only after the wizard commits (see `persistTable`
 * in AddTableWizard).
 */
export const WizardLinkedProducts = ({ pickedIds, onOpenModal, products, links, tables, onDetach }) => {
    const list = Array.from(pickedIds).map((pid) => products.find((p) => p.id === pid)).filter(Boolean);
    return (
        <div className="rounded-lg border border-border overflow-hidden" data-testid="wizard-linked-products">
            <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Package className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
                    <span className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">Bağlı ürünler</span>
                    <Badge variant="outline" className="text-[10px] font-mono num">{list.length}</Badge>
                </div>
                <Button
                    size="sm" variant="outline"
                    className="h-7 text-xs gap-1.5"
                    onClick={onOpenModal}
                    data-testid="wizard-open-link-modal"
                >
                    <Plus className="h-3 w-3" /> Ürün ekle
                </Button>
            </div>
            {list.length === 0 ? (
                <div className="px-4 py-6 text-center">
                    <p className="text-xs text-muted-foreground">Henüz ürün seçmedin — tablo kaydedildikten sonra istediğin zaman bağlayabilirsin.</p>
                </div>
            ) : (
                <div className="divide-y divide-border">
                    {list.map((p) => {
                        const other = links[p.id] ? findTable(tables, links[p.id]) : null;
                        return (
                            <div key={p.id} className="flex items-center gap-3 px-4 py-2" data-testid={`wizard-linked-${p.id}`}>
                                <div className="h-9 w-9 rounded-md border border-border bg-muted overflow-hidden shrink-0">
                                    {p.image ? (
                                        <img src={p.image} alt="" className="h-full w-full object-cover" />
                                    ) : (
                                        <div className="h-full w-full grid place-items-center">
                                            <ImageIcon className="h-3 w-3 text-muted-foreground/40" strokeWidth={1.5} />
                                        </div>
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-xs font-medium truncate">{p.name || <span className="text-muted-foreground italic">İsim yok</span>}</div>
                                    <div className="text-[10.5px] font-mono text-muted-foreground truncate">{p.id}</div>
                                </div>
                                {other && (
                                    <Badge variant="outline" className="text-[9.5px] capitalize border-warning/40 text-warning bg-warning-soft shrink-0">
                                        şu an: {other.name} · taşınacak
                                    </Badge>
                                )}
                                <button
                                    type="button"
                                    onClick={() => onDetach(p.id)}
                                    className="h-7 w-7 rounded-md grid place-items-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                                    aria-label="Listeden çıkar"
                                    title="Listeden çıkar"
                                    data-testid={`wizard-linked-remove-${p.id}`}
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
